/**
 * Converts a time object to a string and removes the last three characters.
 * @param {Date} time - The time object to be converted.
 * @returns {string} The formatted time string.
 */
export function timeToString(time) {
  return time && time.toString().slice(0, -3);
}

/**
 * Converts a time string from 24-hour format to 12-hour format with AM/PM
 * @param {string} string - Time string in 24-hour format (e.g., "15:30")
 * @returns {string} Time string in 12-hour format (e.g., "3:30 PM")
 */
export function convert24To12String(string) {
  if (!string) return "";
  const [hours, minutes] = string.split(":");
  const hour = hours % 12 || 12;
  const period = hours >= 12 ? "PM" : "AM";
  return `${hour}:${minutes} ${period}`;
}

/**
 * Converts a time string from 12-hour format with AM/PM to 24-hour format
 * @param {string} string - Time string in 12-hour format (e.g., "3:30 PM")
 * @returns {string} Time string in 24-hour format (e.g., "15:30")
 */
export function convert12To24String(string) {
  const [time, period] = string.trim().split(" ");
  let [hours, minutes] = time.split(":").map(Number);

  if (period === "PM" && hours !== 12) {
    hours += 12;
  } else if (period === "AM" && hours === 12) {
    hours = 0;
  }

  const formattedHours = String(hours).padStart(2, "0");
  const formattedMinutes = String(minutes).padStart(2, "0");
  return `${formattedHours}:${formattedMinutes}`;
}

/**
 * Parses a 12-hour format time string (e.g., "4:39 PM") into a Date object
 * @param {string} timeString - Time in 12-hour format (e.g., "4:39 PM")
 * @returns {Date} Date object representing the time
 */
export function parseTwelveHourTime(timeString) {
  // Format: "4:39 PM" or "10:15 AM"
  const [timePart, period] = timeString.split(" ");
  let [hours, minutes] = timePart.split(":").map(Number);

  // Convert to 24-hour format
  if (period === "PM" && hours < 12) {
    hours += 12;
  } else if (period === "AM" && hours === 12) {
    hours = 0;
  }

  return new Date(1970, 0, 1, hours, minutes, 0);
}

/**
 * Calculates the difference in hours between two times in 12-hour format
 * @param {string} startTime - Start time in 12-hour format (e.g., "9:30 AM")
 * @param {string} endTime - End time in 12-hour format (e.g., "5:45 PM")
 * @returns {string} The difference in hours as a string with two decimal places
 */
export function hoursDifference12String(startTime, endTime) {
  // Create Date objects
  const start = parseTwelveHourTime(startTime);
  const end = parseTwelveHourTime(endTime);

  // Calculate difference in hours
  const diff = (end - start) / (1000 * 60 * 60); // Convert milliseconds to hours
  return diff.toFixed(2); // Return as a string with two decimal places
}

/**
 * Takes in a time string in 24-hr format, and a float representing a number of hours,
 * returns a string representing the time after adding those hours.
 */
export function addHoursTo24TimeString(timeString, hoursToAdd) {
  const [hours, minutes] = timeString.split(":").map(Number);
  const totalMinutes = hours * 60 + minutes + hoursToAdd * 60;
  const newHours = Math.floor(totalMinutes / 60) % 24;
  const newMinutes = totalMinutes % 60;
  return `${String(newHours).padStart(2, "0")}:${String(newMinutes).padStart(2, "0")}`;
}

/**
 * Converts decimal hours to HH:MM format
 * @param {number|string} decimalHours - Hours as a decimal number or string (e.g., 8.5 or "8.50")
 * @returns {string} Time in HH:MM format (e.g., "08:30")
 */
export function decimalHoursToHHMM(decimalHours) {
  // Convert to number if it's a string
  const hours = parseFloat(decimalHours);

  // Handle invalid input
  if (isNaN(hours) || hours < 0) {
    return "00:00";
  }

  // Calculate hours and minutes
  const wholeHours = Math.floor(hours);
  const minutes = Math.round((hours - wholeHours) * 60);

  // Format with leading zeros
  const formattedHours = String(wholeHours).padStart(2, "0");
  const formattedMinutes = String(minutes).padStart(2, "0");

  return `${formattedHours}:${formattedMinutes}`;
}

/**
 * Removes leading zeros from a time string
 * @param {string} timeString - Time string to process (e.g., "08:30")
 * @returns {string} Time string without leading zeros (e.g., "8:30")
 */
export function removeLeadingZeros(timeString) {
  // Return empty string if timeString is null or undefined
  if (!timeString) {
    return "";
  }
  // Remove leading zeros from the time string
  return timeString.replace(/^0+/, "");
}

/**
 * Converts decimal time to hours and minutes format
 * @param {number|string} decimalTime - Time in decimal format (e.g., 4.17, "4.5")
 * @returns {string} Formatted time string in "Xh Ym" format (e.g., "4h 10m", "47m" for 0:47, "3h" for 3:00)
 */
export function decimalToHoursMinutes(decimalTime, shortFormat = false) {
  // Convert to number if it's a string
  const time = parseFloat(decimalTime);

  // Handle invalid input
  if (isNaN(time) || time < 0) {
    return shortFormat ? "0:00" : "0m";
  }

  // Calculate hours and minutes
  const hours = Math.floor(time);
  const minutes = Math.round((time - hours) * 60);

  if (shortFormat) {
    // Return as h:mm (no leading zeros)
    return `${hours}:${minutes.toString().padStart(2, "0")}`;
  }

  // Handle cases where hours or minutes are 0
  if (hours === 0) {
    return `${minutes}m`;
  } else if (minutes === 0) {
    return `${hours}h`;
  } else {
    return `${hours}h ${minutes}m`;
  }
}

/**
 * Adds two time strings together in HH:mm or H:mm format
 * @param {string} initialTime - Initial time string (e.g., "15:00", "9:30")
 * @param {string} additionTime - Time to add (e.g., "2:30", "0:45")
 * @returns {string} Sum of the two times in HH:mm format (e.g., "17:30")
 */
export function addStringTimes(initialTime, additionTime) {
  if (!initialTime || !additionTime) {
    return initialTime || additionTime || "00:00";
  }

  // Parse initial time
  const [initialHours, initialMinutes] = initialTime.split(":").map(Number);

  // Parse addition time
  const [addHours, addMinutes] = additionTime.split(":").map(Number);

  // Convert everything to minutes for easier calculation
  const totalMinutes =
    initialHours * 60 + initialMinutes + (addHours * 60 + addMinutes);

  // Convert back to hours and minutes
  const resultHours = Math.floor(totalMinutes / 60);
  const resultMinutes = totalMinutes % 60;

  // Format with leading zeros
  const formattedHours = String(resultHours).padStart(2, "0");
  const formattedMinutes = String(resultMinutes).padStart(2, "0");

  return `${formattedHours}:${formattedMinutes}`;
}

/**
 * Gets date in user readable format
 */
export function getFormatedDate(dateString) {
  const date = new Date(dateString);

  const options = {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "numeric",
    minute: "numeric",
    hour12: true,
  };

  let formattedDate = date.toLocaleString("en-US", options);
  formattedDate = formattedDate.replace(",", ", ");
  formattedDate = formattedDate.replace("AM", "a.m.").replace("PM", "p.m.");

  return formattedDate;
}

export function zeroToDash(timeString) {
  return timeString === "00:00" || timeString === "0:00" ? "-" : timeString;
}

/**
 * Parses hours input in various formats to decimal hours
 * Supports: "4.5", "4:30", "4h 30m", "4.5h", etc.
 * @param {string} input - User input string
 * @returns {number|null} - Decimal hours or null if invalid
 */
export function parseHoursInput(input) {
  if (!input || input.trim() === '') return null;
  
  const trimmed = input.trim();
  
  // Try decimal format first (e.g., "4.5")
  const decimalMatch = trimmed.match(/^(\d+\.?\d*)$/);
  if (decimalMatch) {
    const value = parseFloat(decimalMatch[1]);
    return isNaN(value) || value < 0 ? null : value;
  }
  
  // Try time format (e.g., "4:30" or "04:30")
  const timeMatch = trimmed.match(/^(\d+):(\d{2})$/);
  if (timeMatch) {
    const hours = parseInt(timeMatch[1], 10);
    const minutes = parseInt(timeMatch[2], 10);
    if (minutes >= 60 || hours < 0) return null;
    return hours + (minutes / 60);
  }
  
  // Try "4h 30m" format
  const hhmmMatch = trimmed.match(/(\d+)h\s*(\d+)m?/i);
  if (hhmmMatch) {
    const hours = parseInt(hhmmMatch[1], 10);
    const minutes = parseInt(hhmmMatch[2], 10);
    if (minutes >= 60 || hours < 0) return null;
    return hours + (minutes / 60);
  }
  
  return null; // Invalid format
}